import * as React from 'react';
import { keyBy } from 'lodash';
import {
    Agent,
    AssigneeReason,
    AssigneeReasonType,
    IntegrationType,
    IssueBuild,
    IssueEntity,
    IssueSource,
    IssueSourceType,
    IssueState,
} from '@/types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import { TRY_UPDATE_ISSUE_ASSIGNEES, tryUpdateIssueAssignees, TriageViewType } from '@/actions/triage';
import { useError } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { Link } from 'react-router-dom';
import { getIntegrationTypeForAppStoreType } from '@/helpers';
import { displayTextForIssueSourceType, displayUserNameLong } from '@/helpers/displayHelpers';
import { getTriageSeenAt, setTriageSeenAt } from '@/helpers/localStorageHelper';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { BuildSummary, getBuildsForVersion, getIssues } from '@/api';
import { RootState } from '@/reducers/reducers';
import {
    selectAppStoreType,
    selectAuthCredentials,
    selectDemoIssuesForApp,
    selectIsTriageAgentsAvailable,
    selectOrg,
} from '@/selectors/selectors';
import { getDemoBuildsForVersion } from '@/testData/issues';
import CIAppStoreBuildComponent from '@/components/CIAppStoreBuildComponent';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import runwayLogo from '../../../assets/integration-runway.svg';
import { Components } from 'react-markdown';
import { antGrey } from '@/components/styles/globalStyles';

export const ASSIGNEE_USER_PREFIX = 'user:';
export const ASSIGNEE_GROUP_PREFIX = 'group:';
export const ASSIGNEE_AGENT_PREFIX = 'agent:';

export const isAssigneeAgentKey = (key: string) => key.startsWith(ASSIGNEE_AGENT_PREFIX);

const idsWithPrefix = (key: string | undefined, prefix: string) =>
    key?.startsWith(prefix) ? [key.slice(prefix.length)] : [];

export const assigneeIdsFromKey = (key?: string) => ({
    assigneeUserIds: idsWithPrefix(key, ASSIGNEE_USER_PREFIX),
    assigneeGroupIds: idsWithPrefix(key, ASSIGNEE_GROUP_PREFIX),
    assigneeAgentIds: idsWithPrefix(key, ASSIGNEE_AGENT_PREFIX),
});

export const currentAssigneeKey = (issue: IssueEntity) => {
    const userId = issue.assigneeUserIds?.[0];
    const groupId = issue.assigneeGroupIds?.[0];
    const agentId = issue.assigneeAgentIds?.[0];
    return (
        userId ? `${ASSIGNEE_USER_PREFIX}${userId}`
        : groupId ? `${ASSIGNEE_GROUP_PREFIX}${groupId}`
        : agentId ? `${ASSIGNEE_AGENT_PREFIX}${agentId}`
        : undefined
    );
};

export const agentDisplayName = (agentId: string, agent?: Agent) =>
    !agent ? agentId
    : agent.deletedAt ? `${agent.name} (deleted)`
    : agent.name;

// Live agents that cover the app, offered in the assignee pickers; agentsById also holds deleted ones for labels.
export const useIssueAgents = (orgId: string, appId: string) => {
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const isAvailable = useSelector(selectIsTriageAgentsAvailable);
    return React.useMemo(() => {
        const agents = org?.agents ?? [];
        return {
            agentsById: keyBy(agents, (agent) => agent.id),
            assignableAgents:
                isAvailable ? agents.filter((agent) => !agent.deletedAt && agent.appIds.includes(appId)) : [],
        };
    }, [org?.agents, isAvailable, appId]);
};

const AGENT_RETRIGGER_WARNING_MS = 24 * 60 * 60 * 1000;

export const useAssignIssue = (orgId: string, issue: IssueEntity, viewType: TriageViewType) => {
    const dispatch = useDispatch();
    const { agentsById } = useIssueAgents(orgId, issue.appId);
    const error = useError(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);
    React.useEffect(() => {
        if (error) globalMessage.error(error);
    }, [error]);

    return (key?: string) => {
        const assignees = assigneeIdsFromKey(key);
        const submit = () =>
            dispatch(tryUpdateIssueAssignees({ orgId, appId: issue.appId, issueId: issue.id, ...assignees, viewType }));
        const agentId = assignees.assigneeAgentIds[0];
        const triggeredAt = agentId ? issue.agentTriggers?.[agentId]?.at : undefined;
        const recentlyTriggered =
            !!triggeredAt &&
            !(issue.assigneeAgentIds ?? []).includes(agentId) &&
            Date.now() - new Date(triggeredAt).getTime() < AGENT_RETRIGGER_WARNING_MS;
        if (!recentlyTriggered) {
            submit();
            return;
        }
        Modal.confirm({
            title: 'Assign again?',
            content: `${agentDisplayName(agentId, agentsById[agentId])} already started work on this issue in the last 24 hours. Assigning it again starts a new run.`,
            okText: 'Assign again',
            onOk: submit,
        });
    };
};

// Descriptions pulled from tickets embed screenshots at their native size, which overflow the drawer.
export const DESCRIPTION_MARKDOWN_COMPONENTS: Components = {
    img: ({ node: _node, ...props }) => (
        <img
            {...props}
            style={{ maxWidth: '100%', height: 'auto', borderRadius: 4, border: `1px solid ${antGrey.grey5}` }}
        />
    ),
};

export const CLOSED_ISSUE_STATES = [
    IssueState.FixMerged,
    IssueState.FixRejected,
    IssueState.NotAPriority,
    IssueState.ResolvedExternally,
];

export const IssueSourceIcon: React.FC<{
    source: IssueSource | undefined;
    appId: string;
    size?: number;
}> = ({ source, appId, size = 16 }) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    if (source?.type === IssueSourceType.Runway) {
        return <img src={runwayLogo} alt="Team" width={size} height={size} />;
    }
    const derivedIntegrationType =
        source?.type === IssueSourceType.UserReviews && appStoreType ? getIntegrationTypeForAppStoreType(appStoreType)
        : source?.type === IssueSourceType.BetaFeedback ? IntegrationType.TestFlight
        : null;
    return <IntegrationIcon integrationId={derivedIntegrationType ?? source?.integrationType} size={size} />;
};

export const usePendingIssueTransition = (isTransitioning: boolean) => {
    const [pendingTransition, setPendingTransition] = React.useState<IssueState | null>(null);
    React.useEffect(() => {
        if (!isTransitioning) {
            setPendingTransition(null);
        }
    }, [isTransitioning]);
    return { pendingTransition, setPendingTransition };
};

export const issueSourceDisplayText = (source: IssueSource): string =>
    source.displayText || displayTextForIssueSourceType(source.type);

export const sourceOptionLabel = (
    source: IssueSource | undefined,
    appId: string,
    text: string,
    suffix?: React.ReactNode,
): React.ReactNode => (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
        <span style={{ flexShrink: 0, lineHeight: 0 }}>
            <IssueSourceIcon source={source} appId={appId} />
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        {suffix && (
            <span style={{ marginLeft: 'auto', flexShrink: 0, color: antGrey.grey7, fontSize: 12 }}>{suffix}</span>
        )}
    </span>
);

export const useBuildsForVersion = (orgId: string, appId: string, version?: string) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const demoIssues = useSelector((state: RootState) => selectDemoIssuesForApp(state, appId));
    const [builds, setBuilds] = React.useState<BuildSummary[]>([]);
    const [buildsLoading, setBuildsLoading] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (demoIssues) {
            setBuilds(version ? (getDemoBuildsForVersion(demoIssues, version) as BuildSummary[]) : []);
        } else if (version) {
            setBuilds([]);
            setBuildsLoading(true);
            getBuildsForVersion(orgId, appId, version, auth)
                .then((res) => !cancelled && setBuilds(res.builds ?? []))
                .catch(() => !cancelled && setBuilds([]))
                .finally(() => !cancelled && setBuildsLoading(false));
        } else {
            setBuilds([]);
        }
        return () => {
            cancelled = true;
        };
    }, [version, orgId, appId, auth, demoIssues]);

    return { builds, buildsLoading };
};

interface UseTriageNewIssuesProps {
    orgId: string;
    appId: string;
    // appId for the app-level triage page, releaseId for a release's
    scopeId: string;
    // Restricts to issues of this release version; omitted at app level
    version?: string;
    isViewingTriage: boolean;
    skip?: boolean;
}

export const useTriageNewIssues = ({
    orgId,
    appId,
    scopeId,
    version,
    isViewingTriage,
    skip = false,
}: UseTriageNewIssuesProps): number => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [seenAt, setSeenAt] = React.useState(() => getTriageSeenAt(scopeId));
    const [newIssuesCount, setNewIssuesCount] = React.useState(0);

    React.useEffect(() => {
        let storedSeenAt = getTriageSeenAt(scopeId);
        if (!storedSeenAt) {
            storedSeenAt = new Date().toISOString();
            setTriageSeenAt(scopeId, storedSeenAt);
        }
        setSeenAt(storedSeenAt);
    }, [scopeId]);

    React.useEffect(() => {
        if (!isViewingTriage) {
            return undefined;
        }
        const markSeen = () => {
            const now = new Date().toISOString();
            setTriageSeenAt(scopeId, now);
            setSeenAt(now);
        };
        markSeen();
        // Issues that came in while the page was open have been seen too.
        return markSeen;
    }, [isViewingTriage, scopeId]);

    React.useEffect(() => {
        if (skip || isViewingTriage || !seenAt || !auth) {
            setNewIssuesCount(0);
            return undefined;
        }
        // The app page lists every release's issues, so seeing it also counts as seeing each release's.
        const appSeenAt = scopeId === appId ? '' : getTriageSeenAt(appId);
        const detectedAfter = appSeenAt > seenAt ? appSeenAt : seenAt;
        let cancelled = false;
        getIssues({ orgId, appId, detectedAfter, versions: version ? [version] : undefined }, 1, 0, auth)
            .then((res) => {
                if (!cancelled) {
                    setNewIssuesCount(res.openCount + res.closedCount);
                }
            })
            .catch(() => {
                // The badge is a hint; a failed check just leaves it off.
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, appId, scopeId, version, seenAt, skip, isViewingTriage, auth]);

    return newIssuesCount;
};

export const buildSelectOptions = (builds: IssueBuild[]) =>
    builds.map((b) => ({
        value: b.identifier || (b.appStoreBuild ?? ''),
        searchLabel: [b.identifier, b.appStoreBuild].filter(Boolean).join(' '),
        label: (
            <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <CIAppStoreBuildComponent
                    ciBuildIdentifier={b.identifier}
                    ciBuildIntegrationId={b.integrationId as IntegrationType}
                    appStoreBuildIdentifier={b.appStoreBuild}
                    appStoreBuildIntegrationId={b.appStoreIntegrationId as IntegrationType}
                    isAppStoreBuildProcessed={b.appStoreBuildIsProcessed ?? true}
                    strong={false}
                />
            </span>
        ),
    }));

export const sortUsersBySuggestedFirst = <T extends { id: string }>(users: T[], suggestedUserIds?: string[]): T[] => {
    if (!suggestedUserIds?.length) {
        return users;
    }
    const rank = (user: T) => {
        const index = suggestedUserIds.indexOf(user.id);
        return index === -1 ? suggestedUserIds.length : index;
    };
    return [...users].sort((a, b) => rank(a) - rank(b));
};

const TOOLTIP_LINK_STYLE: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };
const REASON_REF_LABEL_MAX_LENGTH = 60;

const AssigneeReasonRef: React.FC<{ reason: AssigneeReason; basePath?: string }> = ({ reason, basePath }) => {
    const label =
        reason.refLabel.length > REASON_REF_LABEL_MAX_LENGTH ?
            `${reason.refLabel.slice(0, REASON_REF_LABEL_MAX_LENGTH).trimEnd()}…`
        :   reason.refLabel;
    if (reason.refUrl) {
        return (
            <a href={reason.refUrl} target="_blank" rel="noreferrer" style={TOOLTIP_LINK_STYLE} title={reason.refLabel}>
                {label}
            </a>
        );
    }
    if (reason.refIssueId && basePath) {
        return (
            <Link to={`${basePath}/${reason.refIssueId}`} style={TOOLTIP_LINK_STYLE} title={reason.refLabel}>
                {label}
            </Link>
        );
    }
    return <span title={reason.refLabel}>{label}</span>;
};

const describeAssigneeReason = (reason: AssigneeReason, basePath?: string): React.ReactNode => {
    const ref = <AssigneeReasonRef reason={reason} basePath={basePath} />;
    switch (reason.type) {
        case AssigneeReasonType.CauseAuthor:
            return <>author of {ref} cited as a potential cause</>;
        case AssigneeReasonType.FeatureFlagOwner:
            return <>owner of feature flag {ref} cited as a potential cause</>;
        case AssigneeReasonType.TicketAssignee:
            return <>assignee of {ref}</>;
        case AssigneeReasonType.RelatedIssueOwner:
            return <>owner of related issue {ref}</>;
        case AssigneeReasonType.RelatedTicketAssignee:
            return <>assignee of {ref} on a related issue</>;
        case AssigneeReasonType.ReleasePilot:
            return <>release pilot of {reason.refLabel}</>;
        default:
            return null;
    }
};

export const AutoAssignmentReasons: React.FC<{ issue: IssueEntity; basePath?: string }> = ({ issue, basePath }) => {
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const assigneeId = issue.assigneeUserIds?.[0];
    const assignedReason = assigneeId ? issue.assignmentReasons?.[assigneeId] : undefined;
    if (!issue.isAutoAssigned || !assigneeId || !assignedReason) return null;

    const nameOf = (userId: string) => {
        const user = appUsers.find((u) => u.id === userId);
        return user ? displayUserNameLong(user) : userId;
    };
    const others = (issue.suggestedAssigneeUserIds ?? [])
        .filter((userId) => userId !== assigneeId && issue.assignmentReasons?.[userId])
        .map((userId) => ({ userId, reason: issue.assignmentReasons![userId] }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>
                Assigned to <b>{nameOf(assigneeId)}</b>, {describeAssigneeReason(assignedReason, basePath)}
            </span>
            {others.length > 0 && (
                <>
                    <span>Other candidates</span>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {others.map(({ userId, reason }) => (
                            <li key={userId}>
                                <b>{nameOf(userId)}</b>, {describeAssigneeReason(reason, basePath)}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export const hasAutoAssignmentReasons = (issue: IssueEntity): boolean => {
    const assigneeId = issue.assigneeUserIds?.[0];
    return !!issue.isAutoAssigned && !!assigneeId && !!issue.assignmentReasons?.[assigneeId];
};
